import type { AxisConfiguration } from 'components/molecules/PackArchitecture/AxisConfigurationModal';
import type { KpiAxisConfiguration } from 'components/molecules/PackArchitecture/KpiAxisConfigurationModal';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import type { KpiBinLabelAffixes, KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import type { WhitespaceValueSalesLegendData } from 'components/pages/WhitespaceAnalysis/whitespaceGridKpiAxis.helpers';
import type { WhitespaceAxisDropdownConfig } from 'components/pages/WhitespaceAnalysis/whitespaceMeta.helpers';
import { DEFAULT_KPI_DATA_RANGE } from 'constants/ppa';
import type { WhitespaceGridRow } from 'hooks/useWhitespaceAnalysisGridData';
import type React from 'react';

export type WhitespaceOptionItem = {
    id_type?: string;
    id?: string;
    label?: string;
    dimension?: string | null;
    /** Display label for the dimension this option belongs to (e.g. 'Product', 'Geo', 'Price', 'Pack') - drives axis dropdown grouping. */
    dimension_label?: string | null;
    is_attribute?: boolean | null;
    is_default?: boolean;
    is_mandatory?: boolean;
    /**
     * Companion option(s) for this option (e.g. Size_Range -> [{ id: 'ws_pack_size_rank',
     * id_type: 'kpi_id' }]). When a `kpi_id`-typed entry is present, it's fetched/displayed
     * as the secondary cell metric instead of the default axis KPI.
     */
    options?: WhitespaceOptionItem[];
};

export type WhitespaceDropdownConfig = WhitespaceAxisDropdownConfig & {
    group_by_options?: WhitespaceOptionItem[];
    column_options?: WhitespaceOptionItem[];
};

export type WhitespaceMetaResponse = {
    title?: string;
    tooltip?: string;
    options?: {
        drop_down?: WhitespaceDropdownConfig[];
        dropdowns?: WhitespaceDropdownConfig[];
        toggle?: Array<{
            id?: string;
            label?: string;
            default_enabled?: boolean;
            input_list?: Array<{
                input_id?: string;
                default_value?: boolean;
                placeholder?: string;
                tooltip?: string;
            }>;
        }>;
        child_limit?: number;
        column_limit?: number;
        row_limit?: number;
    };
    page_limit?: number;
    child_limit?: number;
    column_limit?: number;
    row_limit?: number;
};

export type WhitespaceAnalysisContextValue = {
    title: string;
    tooltip: string;
    whitespaceToggleTooltip: string;
    showNoFilterSelection: boolean;
    xAxis: string;
    setXAxis: React.Dispatch<React.SetStateAction<string>>;
    yAxis: string;
    setYAxis: React.Dispatch<React.SetStateAction<string>>;
    selectedYAxisKpiId: string;
    crossSection: string;
    setCrossSection: React.Dispatch<React.SetStateAction<string>>;
    showWhitespaces: boolean;
    setShowWhitespaces: React.Dispatch<React.SetStateAction<boolean>>;
    xAxisOptions: string[];
    yAxisOptions: string[];
    crossSectionOptions: string[];
    /** Raw X/Y axis option items (carrying `dimension_label`), used to build grouped dropdown option panels. */
    xAxisOptionItems: WhitespaceOptionItem[];
    yAxisOptionItems: WhitespaceOptionItem[];
    xAxisFilter: {
        dimension: string;
        attributeType: string;
        isAttribute: boolean;
    };
    childLimit: number;
    gridRequestPayload: Record<string, unknown>;
    columnHeaders: string[];
    gridRows: WhitespaceGridRow[];
    loadMoreRows: () => Promise<void>;
    loadMoreColumns: () => Promise<void>;
    hasMoreRows: boolean;
    hasMoreColumns: boolean;
    isFetchingMoreRows: boolean;
    isFetchingMoreColumns: boolean;
    isMetaLoading: boolean;
    isGridLoading: boolean;
    isGridFetching: boolean;
    hasGridError: boolean;
    isGridFetched: boolean;
    totalColumnCount: number;
    totalRowCount: number;
    xAxisDimensionConfig?: AxisConfiguration;
    setXAxisDimensionConfig: React.Dispatch<React.SetStateAction<AxisConfiguration | undefined>>;
    applyXAxisDimensionConfig: (config: AxisConfiguration) => void;
    yAxisKpiConfig?: KpiAxisConfiguration;
    setYAxisKpiConfig: React.Dispatch<React.SetStateAction<KpiAxisConfiguration | undefined>>;
    applyYAxisKpiConfig: (config: KpiAxisConfiguration) => void;
    /** Y-axis attribute (e.g. Pack_Range) dimension config - mirrors xAxisDimensionConfig, populated only when isYAxisAttribute is true. */
    yAxisDimensionConfig?: AxisConfiguration;
    setYAxisDimensionConfig: React.Dispatch<React.SetStateAction<AxisConfiguration | undefined>>;
    applyYAxisDimensionConfig: (config: AxisConfiguration) => void;
    /** True when the selected Y option is a config-driven attribute (is_y_axis_attribute), e.g. Pack_Range, rather than a KPI. */
    isYAxisAttribute: boolean;
    xAxisGroupByControlsMeta: AxisGroupByControlsMeta | null;
    yAxisGroupByControlsMeta: AxisGroupByControlsMeta | null;
    yAxisKpiControlsMeta: KpiAxisControlsMeta | null;
    yAxisKpiDataRange: KpiDataRange;
    yAxisKpiBinLabelAffixes: KpiBinLabelAffixes;
    yAxisKpiOptionIds: string[];
    valueSalesLegend: WhitespaceValueSalesLegendData | null;
};

export const EMPTY_WHITESPACE_CONTEXT: WhitespaceAnalysisContextValue = {
    title: '',
    tooltip: '',
    whitespaceToggleTooltip: '',
    showNoFilterSelection: false,
    xAxis: '',
    setXAxis: () => undefined,
    yAxis: '',
    setYAxis: () => undefined,
    selectedYAxisKpiId: '',
    crossSection: '',
    setCrossSection: () => undefined,
    showWhitespaces: false,
    setShowWhitespaces: () => undefined,
    xAxisOptions: [],
    yAxisOptions: [],
    crossSectionOptions: [],
    xAxisOptionItems: [],
    yAxisOptionItems: [],
    xAxisFilter: {
        dimension: '',
        attributeType: '',
        isAttribute: false,
    },
    childLimit: 0,
    gridRequestPayload: {},
    columnHeaders: [],
    gridRows: [],
    loadMoreRows: async () => undefined,
    loadMoreColumns: async () => undefined,
    hasMoreRows: false,
    hasMoreColumns: false,
    isFetchingMoreRows: false,
    isFetchingMoreColumns: false,
    isMetaLoading: true,
    isGridLoading: false,
    isGridFetching: false,
    hasGridError: false,
    isGridFetched: false,
    totalColumnCount: 0,
    totalRowCount: 0,
    xAxisDimensionConfig: undefined,
    setXAxisDimensionConfig: () => undefined,
    applyXAxisDimensionConfig: () => undefined,
    yAxisKpiConfig: undefined,
    setYAxisKpiConfig: () => undefined,
    applyYAxisKpiConfig: () => undefined,
    yAxisDimensionConfig: undefined,
    setYAxisDimensionConfig: () => undefined,
    applyYAxisDimensionConfig: () => undefined,
    isYAxisAttribute: false,
    xAxisGroupByControlsMeta: null,
    yAxisGroupByControlsMeta: null,
    yAxisKpiControlsMeta: null,
    yAxisKpiDataRange: { ...DEFAULT_KPI_DATA_RANGE },
    yAxisKpiBinLabelAffixes: {},
    yAxisKpiOptionIds: [],
    valueSalesLegend: null,
};

export type WhitespaceAnalysisContextProps = {
    children: React.ReactNode;
};

export const EMPTY_DROPDOWNS: WhitespaceDropdownConfig[] = [];
export const EMPTY_OPTION_ITEMS: WhitespaceOptionItem[] = [];
export const EMPTY_GRID_REQUEST: Record<string, unknown> = {};
